import { CaseAudit, CaseDetails } from "@/types"
import api from "./api"

/**
 * Manages the audit history of a Case
 */
export default class CaseAuditHistoryService {
	private currentCaseId: string | null = null
	private _auditHistory: CaseAudit[] | null = null
	private refreshVersion = 0

	private refreshing = false
	private refreshQueued = false
	private refreshTimeout: NodeJS.Timeout | null = null

	public get auditHistory (): CaseAudit[] | null {
		return this._auditHistory
	}

	public async load (currentCase: CaseDetails, params: CaseAuditHistoryServiceLoadParams = {}) {
		const nextCaseId = currentCase?.id ?? null
		if (this.currentCaseId !== nextCaseId) {
			this.refreshVersion += 1
			this._auditHistory = null
		}

		this.currentCaseId = nextCaseId
		if (!params.skipInitialFetch) {
			await this.refresh()
		}
	}

	public async refresh () {
		if (!this.currentCaseId) {
			this.clear()
			return
		}

		if (this.refreshing) {
			this.refreshQueued = true
			return
		}

		const caseId = this.currentCaseId
		const refreshVersion = this.refreshVersion
		const currentAuditHistory = this.auditHistory

		this.refreshing = true
		this.refreshQueued = false

		try {
			if (currentAuditHistory && currentAuditHistory.length > 0) {
				const lastAudit = currentAuditHistory[0].changedAt
				const newEntries = await api.history(caseId, lastAudit)

				if (this.refreshVersion === refreshVersion && this.currentCaseId === caseId) {
					this._auditHistory = newEntries.concat(currentAuditHistory)
				}
			} else {
				const auditHistory = await api.history(caseId)

				if (this.refreshVersion === refreshVersion && this.currentCaseId === caseId) {
					this._auditHistory = auditHistory
				}
			}
		} finally {
			this.refreshing = false

			if (this.refreshQueued && this.currentCaseId) {
				this.refreshQueued = false
				if (!this.refreshTimeout) {
					this.refreshTimeout = setTimeout(() => {
						this.refreshTimeout = null
						this.refresh()
					}, 10)
				}
			}
		}
	}

	public clear () {
		this.refreshVersion += 1
		this._auditHistory = null
		this.currentCaseId = null
		this.refreshQueued = false
		if (this.refreshTimeout) {
			clearTimeout(this.refreshTimeout)
			this.refreshTimeout = null
		}
	}
}

interface CaseAuditHistoryServiceLoadParams {
	skipInitialFetch?: boolean
}
